import { createRoot } from 'react-dom/client';

import { CommanderActionTypesEnum, createCommander } from '@spazious/dollhouse-commander';
import { createDollhouseStore } from '@spazious/dollhouse-engine';
import {
  createFSetupGraph,
  FSetupFetchActionType,
  FSetupFetchMethodFunction,
} from '@spazious/dollhouse-fsetup';
import { createHistory } from '@spazious/dollhouse-history';
import { defaultSchemaFetchMethod } from '@spazious/dollhouse-mock';
import { init } from '@spazious/dollhouse-navmesh';
import { createBrowserRouter } from '@spazious/dollhouse-router';
import {
  createStore as createShallowGraphStore,
  FShallowNodeTypesEnum,
  GraphAction,
  GraphActionType,
  migrateGraph,
  shallowInitState,
} from '@spazious/dollhouse-shallow-graph';
import {
  App,
  createEvents,
  createFeatureChecker,
  paramsStore,
  stagingHashFn,
  StateParams,
  updateNodesStacking,
  useDeviceInfo,
} from '@spazious/dollhouse-shared';
import { createStagingArea } from '@spazious/dollhouse-staging-area';
import { createStore, storePersistKeys } from '@spazious/dollhouse-store';
import { World } from '@spazious/dollhouse-three';
import { ControlsModeEnum, FeatureTags, ModeTypeEnum } from '@spazious/dollhouse-types';
import { normalize } from '@spazious/jsonapi-tools';
import { createClient } from '@spazious/react-booking-engine';
import sentry from '@spazious/vite-sentry';

import { Main } from './components/Main/index.js';
import {
  PLANNER_FEATURES_MODE_CREATION,
  PLANNER_FEATURES_MODE_EDIT,
  PLANNER_FEATURES_MODE_READ_ONLY,
  PLANNER_FEATURES_MODE_RTS_GUEST,
  PLANNER_MOBILE_FEATURES_MODE_CREATION,
  PLANNER_MOBILE_FEATURES_MODE_EDIT,
  PLANNER_MOBILE_FEATURES_MODE_READ_ONLY,
  PLANNER_MOBILE_FEATURES_MODE_RTS_GUEST,
} from './constants/features.js';
import { createApolloClient } from './graphql/client.js';
import routes from './routes.js';

import '@spazious/dollhouse-engine/extends';

await init();
// Intialize sentry
sentry(import.meta.env.SPZ_APP_ENVIRONMENT, import.meta.env.SPZ_APP_SENTRY_DSN);

const spaceId = new URLSearchParams(window.location.search).get('space');
const hotelId = new URLSearchParams(window.location.search).get('hotel');

const basePath = new URL(import.meta.env.SPZ_APP_API_URL).origin;

const apiClient = createClient({
  basePath,
});

const fetchFurnitureMethod = async (id: number) => {
  const result = await apiClient.diagramming.retrieveDiagrammingVirtualFurnituresId({
    id: id.toString(),
  });
  const asset = result.data.data.attributes?.asset;
  const name = result.data.data.attributes?.name;
  const furnitureType = result.data.data.attributes?.furnitureType;
  if (!asset) {
    throw new Error('Asset not defined for this furniture');
  }
  if (!name) {
    throw new Error('Name not defined for this furniture');
  }
  return { url: asset, name, furnitureType } as unknown as string;
};

const fetchDecorationMethod = async (id: number) => {
  const result = await apiClient.diagramming.retrieveDiagrammingVirtualObjectsId({
    id: id.toString(),
  });

  const { asset, name, objectType } = result.data.data.attributes ?? {};

  if (!asset) {
    throw new Error('Asset not defined for this furniture');
  }
  if (!name) {
    throw new Error('Name not defined for this furniture');
  }
  return { url: asset, name, decorationType: objectType } as unknown as string;
};

const fetchMethod = ((type, id) => {
  switch (type) {
    case FSetupFetchActionType.Furniture: {
      return fetchFurnitureMethod(id);
    }
    case FSetupFetchActionType.Decoration: {
      return fetchDecorationMethod(id);
    }
    default: {
      return defaultSchemaFetchMethod(type, id);
    }
  }
}) as FSetupFetchMethodFunction;

const dollhouse = createDollhouseStore();

const staging = createStagingArea<GraphAction, CommanderActionTypesEnum>([], {
  hashActionFn: stagingHashFn,
});

const graphqlClient = createApolloClient({
  get api() {
    return apiClient;
  },
  get graph() {
    return graph;
  },
});

const shallow = createShallowGraphStore();

const graph = createFSetupGraph(fetchMethod, graphqlClient, shallow);

const history = createHistory<GraphAction[], CommanderActionTypesEnum>(
  [
    {
      type: GraphActionType.Init,
      payload: {
        ...shallowInitState,
        space: spaceId ? parseInt(spaceId) : 0,
      },
    },
  ],
  CommanderActionTypesEnum.Init,
  {
    wif: import.meta.env.SPZ_APP_HISTORY_WIF,
  }
);

const commander = createCommander(shallow, history, staging, {
  onBeforeCommit: (shallow) => updateNodesStacking(shallow, graph),
});

const world = new World();

const store = createStore(dollhouse, graph, staging, history, world, commander, {
  name: 'planner',
  devtools: import.meta.env.DEV,
  persistKeys: storePersistKeys.filter((key) => key !== 'history'),
  initializer: async ({ getState }) => {
    const { setupGraphId } = paramsStore.getState();
    let newState = {};
    let templatesCount: number | null = null;
    // Planner create mode
    let plannerParams: StateParams = {
      spaceId: null,
      mode: ModeTypeEnum.Create,
      setupGraphId: null,
      hotelId: hotelId,
    };
    // Planner edit / readonly mode
    if (setupGraphId) {
      const { data } = await apiClient.diagramming.retrieveDiagrammingVirtualMeetingRoomSetupsId({
        id: setupGraphId,
        include: 'virtualMeetingRoom',
      });
      const normalizedData = normalize(data as any) as any;
      const isReadOnlyMode = normalizedData?.permission === ModeTypeEnum.ReadOnly;
      templatesCount = normalizedData?.virtualMeetingRoom?.templatesCount ?? null;

      plannerParams = {
        ...plannerParams,
        mode: isReadOnlyMode ? ModeTypeEnum.ReadOnly : ModeTypeEnum.Edit,
      };

      if (normalizedData?.graph && normalizedData?.virtualMeetingRoom?.id) {
        history.reset(
          [
            {
              type: GraphActionType.Init,
              payload: {
                ...migrateGraph(normalizedData.graph),
                space: parseInt(normalizedData?.virtualMeetingRoom?.id),
              },
            },
          ],
          CommanderActionTypesEnum.Init
        );
      }
    } else if (spaceId) {
      const { data } = await apiClient.diagramming.retrieveDiagrammingVirtualMeetingRoomId({
        id: spaceId,
      });
      const normalizedData = normalize(data as any) as any;
      templatesCount = normalizedData?.templatesCount ?? null;

      if (normalizedData?.initialGraph) {
        history.reset(
          [
            {
              type: GraphActionType.Init,
              payload: {
                ...shallowInitState,
                ...migrateGraph(normalizedData.initialGraph),
                space: parseInt(spaceId),
              },
            },
          ],
          CommanderActionTypesEnum.Init
        );
      }

      plannerParams = {
        ...plannerParams,
        mode: ModeTypeEnum.Create,
      };
    }

    paramsStore.setState(plannerParams);
    return {
      ...newState,
      // [SPZD-733] disabling camera fly through temporarily
      controls: !getState().settings.disableCameraTravelling
        ? ControlsModeEnum.Travelling
        : ControlsModeEnum.Orbit,
      templatesCount,
    };
  },
});

world.on('ready', () => {
  store.setState({ status: 'ready' });
});

const isMobile = useDeviceInfo.getState().isMobile;

export const featuresModeMapping = {
  [ModeTypeEnum.Create]: isMobile
    ? PLANNER_MOBILE_FEATURES_MODE_CREATION
    : PLANNER_FEATURES_MODE_CREATION,
  [ModeTypeEnum.Edit]: isMobile ? PLANNER_MOBILE_FEATURES_MODE_EDIT : PLANNER_FEATURES_MODE_EDIT,
  [ModeTypeEnum.ReadOnly]: isMobile
    ? PLANNER_MOBILE_FEATURES_MODE_READ_ONLY
    : PLANNER_FEATURES_MODE_READ_ONLY,
  [ModeTypeEnum.RemoteSession]: isMobile
    ? PLANNER_FEATURES_MODE_RTS_GUEST
    : PLANNER_MOBILE_FEATURES_MODE_RTS_GUEST,
} as const;

const nodeFeatureMapping: Record<number, FeatureTags | FeatureTags[]> = {
  [FShallowNodeTypesEnum.FComposition]: 'graphs.setup.node',
  [FShallowNodeTypesEnum.FGroup]: 'graphs.setup.node',
  [FShallowNodeTypesEnum.Furniture]: 'graphs.setup.node',
  [FShallowNodeTypesEnum.FPolygon]: 'graphs.setup.node',
  [FShallowNodeTypesEnum.FArea]: ['graphs.setup.node', 'graphs.setup.node.farea'],
  [FShallowNodeTypesEnum.CameraPath]: ['graphs.setup.node', 'graphs.setup.node.camerapath'],
  [FShallowNodeTypesEnum.Measurement]: ['graphs.setup.node', 'graphs.setup.node.measurement'],
  [FShallowNodeTypesEnum.Decoration]: ['graphs.setup.node', 'graphs.setup.node.decoration'],
  [FShallowNodeTypesEnum.Annotation]: ['graphs.setup.node', 'graphs.setup.node.annotation'],
  [FShallowNodeTypesEnum.FOverride]: ['graphs.setup.node', 'graphs.setup.node'],
};

paramsStore.subscribe((params) => {
  const enabledFeatures = featuresModeMapping[params.mode];

  if (!enabledFeatures) {
    throw new Error(`No features found for mode: ${params.mode}`);
  }

  const hasFeature = createFeatureChecker(enabledFeatures);

  store.setState({ features: enabledFeatures });

  graph.layers.disableAll();

  // Calculate enabled nodes based on features
  let enabledNodes = Object.entries(nodeFeatureMapping).reduce(
    (acc, [nodeType, featureName]) =>
      hasFeature(featureName) ? [...acc, nodeType as any as FShallowNodeTypesEnum] : acc,
    [] as FShallowNodeTypesEnum[]
  );

  graph.layers.enable(...enabledNodes);

  // In read-only mode, keep annotations/measurements available but hidden by default.
  if (params.mode === ModeTypeEnum.ReadOnly) {
    graph.layers.disable(FShallowNodeTypesEnum.Measurement, FShallowNodeTypesEnum.Annotation);
  }
});

const router = createBrowserRouter(routes);

const events = createEvents();

createRoot(document.getElementById('root')! as Element).render(
  <App
    client={graphqlClient}
    events={events}
    graph={graph}
    main={<Main />}
    router={router}
    store={store}
  />
);

store.persist.onFinishHydration(() => {
  const sessionId = new URLSearchParams(window.location.search).get('ssid');

  if (sessionId) {
    paramsStore.setState({
      mode: ModeTypeEnum.RemoteSession,
    });

    store.rts
      .getState()
      .connect(sessionId)
      .then(null, (err: Error) => {
        events.emit('error', `Something wrong happened: ${err.message}`);
      });
  }
});
